import type { AgentType, RefCast } from "@productlane/lib"
import { Input } from "@productlane/ui"

import type { ChainStep as Step } from "../model/fallback-step"
import type { RefsState } from "../model/refs-state"
import type { SecureRefOption } from "../model/secure-ref-picker"
import { SecureRefAutocomplete } from "../inputs/secure-ref-autocomplete"
import { StepNotices } from "./step-notices"

interface Props {
	name: string
	step: Step
	/** The value is a JSON document, so a template gets the roomy editor. */
	writesJson?: boolean
	cast?: RefCast
	agentTypes: AgentType[]
	/** Property a typed token starts on, when the argument holds properties. */
	agentProperty?: string
	knownKeys?: readonly string[]
	refOptions: readonly SecureRefOption[]
	refsState: RefsState
	onRetryRefs: () => void
	onChange: (value: string, save?: boolean) => void
	/** A ref field cannot hold a token, so picking one swaps this step to a template. */
	onPickAgent: (template: string) => void
}

/** One link of a rule's chain: its value, plus what is wrong with it. */
export function ChainStep({
	name,
	step,
	writesJson,
	cast,
	agentTypes,
	agentProperty,
	knownKeys,
	refOptions,
	refsState,
	onRetryRefs,
	onChange,
	onPickAgent,
}: Props) {
	if (step.mode === "fixed") {
		return (
			<Input
				name={name}
				value={step.value}
				textSize="sm"
				placeholder="cus_default"
				onChange={(event) => onChange(event.target.value, false)}
				onBlur={() => onChange(step.value)}
			/>
		)
	}

	return (
		<div className="space-y-1">
			<SecureRefAutocomplete
				name={name}
				mode={step.mode}
				value={step.value}
				// A template that resolves to JSON is a document, so it gets the same room
				// the fixed-value editor gives one.
				multiline={step.mode === "template" && writesJson}
				options={refOptions}
				agentTypes={agentTypes}
				agentProperty={agentProperty}
				refsState={refsState}
				onRetryRefs={onRetryRefs}
				onChange={(next) => onChange(next, false)}
				onCommit={(next) => onChange(next)}
				onPickAgent={onPickAgent}
			/>
			<StepNotices
				value={step.value}
				mode={step.mode}
				options={refOptions}
				knownKeys={knownKeys}
				onRewrite={(next) => onChange(next)}
				cast={cast}
			/>
		</div>
	)
}
